import React from 'react';
import { Callout, Step, DocHeading, DocTable, Shot } from './components';

function PageAutoLoot({ navigate }) {
  return (
    <div>
      <p className="text-sm text-brand-400 font-medium mb-2">Farm</p>
      <h1 className="font-display text-3xl font-bold text-white tracking-tight mb-3">Auto Loot</h1>
      <p className="text-gray-400 leading-relaxed mb-8">
        Auto Loot picks up the items lying around you, filtered by what you actually want. It does
        not walk to anything &mdash; it takes what is within reach as you pass. Everything is on the{' '}
        <strong className="text-white">FARM &rsaquo; Loot</strong> tab.
      </p>

      <Callout type="info">
        This is the easiest feature to see working, so it is a good first test after injecting.
        Switch it on, kill something, and watch <strong>Picked up</strong> in the{' '}
        <strong>THIS SESSION</strong> card go up.
      </Callout>

      <DocHeading>Turning It On</DocHeading>
      <Step n={1} title="Switch on AUTO LOOT">
        The switch sits on the right of the <strong>AUTO LOOT</strong> card&rsquo;s title row &mdash;
        click anywhere on that row. Below it you can bind a <strong>Toggle key</strong>. The key
        works even while the feature is off: one press switches it on or off without opening the
        menu. It only reacts while the game window is in front, so typing in another program does
        not trigger it.
      </Step>
      <Step n={2} title="Set the range">
        In the <strong>RANGE</strong> card, drag <strong>Pick up within</strong> or click one of the
        three presets: <strong>GAME</strong>, <strong>5</strong> or <strong>MAX</strong>.
      </Step>
      <Step n={3} title="Choose what to pick up">
        In the <strong>WHAT TO PICK UP</strong> card, tick the categories you want in the{' '}
        <strong>Categories</strong> dropdown. Anything not covered there stays on the ground.
      </Step>

      <Shot
        tab="FARM › Loot"
        caption="The Loot tab: status and range on the left, the pickup filter and session counters on the right."
        name="auto-loot-01-loot-tab.png"
        shoot="A FARM › Loot fül bekapcsolt Auto Loot-tal, egy pályán, ahol tárgyak hevernek a közelben. Balra az AUTO LOOT kártya (zöld kapcsoló a címsorban, zöld Status, In range / Matching filter számok, Bag free) és alatta a RANGE kártya (az ADVANCED lehet csukva). Jobbra a WHAT TO PICK UP kártya a Categories legördülővel és legalább egy al-szűrővel, alatta a THIS SESSION kártya nem nulla Picked up értékkel."
      />

      <DocHeading>The AUTO LOOT Card</DocHeading>
      <DocTable
        headers={['Line', 'What it means']}
        rows={[
          [<strong className="text-white">Status</strong>, 'What it is doing right now (see below). Green while the feature is on, red if it stopped itself.'],
          [<strong className="text-white">In range</strong>, 'How many items are lying within your range.'],
          [<strong className="text-white">Matching filter</strong>, 'How many of those your settings allow. If this is 0 while In range is not, your filter is doing its job — or it is too strict.'],
          [<strong className="text-white">On cooldown</strong>, 'Shown only when not zero. Matching items it is holding back right now: mostly ones it already asked for that are still on the ground and waiting for another try, plus any that did not fit into the current burst.'],
          [<strong className="text-white">Needs a free slot</strong>, 'Shown only when not zero. Gear it wants but cannot take, because the bag is full.'],
          [<strong className="text-white">Bag free</strong>, 'Free bag slots out of the total. Turns orange at zero.'],
        ]}
      />

      <DocTable
        headers={['Status', 'What it means']}
        rows={[
          ['Disabled', 'Auto Loot is switched off.'],
          ['Nothing to pick up', 'Nothing within range matches your settings.'],
          ['Picking up...', 'A pickup request is on its way.'],
          ['Waiting (interval)', 'Something matches; it is waiting for the delay between bursts to pass.'],
          ['Idle', 'Nothing matches right now, and the delay between bursts has not passed yet.'],
          ['All nearby items on cooldown', 'Everything that matches has just been tried and is waiting for another attempt.'],
          ['Bag full', 'The only matching items are gear, and there is no free slot for them.'],
          ['Not connected', 'You are not connected to a game server (loading screen, disconnect).'],
          ['Waiting for game thread', 'The bot has not linked up with the game yet — normal for a moment right after injecting. If it never goes away, close the game and inject again.'],
          ['Waiting for game data', 'The bot cannot read the world yet — usually right after injecting or during loading.'],
          ['Item data looks wrong - paused', 'What the bot reads about items does not make sense, so it stopped. This usually means a game patch; wait for an update.'],
          ['Disabled: unexpected command layout', 'The game changed how pickups are sent, so the bot locked itself rather than send something wrong. Wait for an update.'],
        ]}
      />

      <Callout type="warning">
        <strong>&ldquo;Game tick not firing - move your character.&rdquo;</strong> The bot works in
        step with the game. Standing perfectly still in a menu can pause that; one step fixes it.
      </Callout>
      <Callout type="warning">
        <strong>&ldquo;Game thread not identified - nothing is being sent. Reload the DLL.&rdquo;</strong>{' '}
        The bot could not hook into the game properly, so no pickup goes out. Close the game and
        inject again.
      </Callout>
      <Callout type="warning">
        <strong>&ldquo;Stopped: the pickup command does not look like it used to. Probably a game
        patch.&rdquo;</strong> The bot locked itself down rather than send something wrong. Wait
        for an update.
      </Callout>

      <DocHeading>The THIS SESSION Card</DocHeading>
      <DocTable
        headers={['Line', 'What it means']}
        rows={[
          [<strong className="text-white">Picked up</strong>, 'How many pickup requests were sent since you started the bot.'],
          [<strong className="text-white">In range now</strong>, 'The same number as In range above.'],
          [<strong className="text-white">Matching the filter</strong>, 'The same number as Matching filter above.'],
        ]}
      />

      <DocHeading>Range</DocHeading>
      <DocTable
        headers={['Control', 'What it does']}
        rows={[
          [<strong className="text-white">Pick up within</strong>, 'How far it may pick up from: 1 to 6 metres. Default 5 m.'],
          [<strong className="text-white">GAME</strong>, 'Sets the game’s own auto-pickup distance (normally 2.25 m).'],
          [<strong className="text-white">5</strong>, 'Sets the game’s larger (premium) auto-pickup distance, 5 m.'],
          [<strong className="text-white">MAX</strong>, 'Sets 6 m — or, once at least three pickups have failed from a shorter distance and none has worked from that far, that distance (never below 3 m).'],
          [<strong className="text-white">Confirmed up to</strong>, 'Measured, not promised: the furthest distance a pickup has actually worked from, and how many items that is based on. Shows “no data yet” until the first success.'],
          [<strong className="text-white">Failed from</strong>, 'The shortest distance from which an item was still on the ground a few seconds after the request. Only shown once that has happened.'],
        ]}
      />
      <Callout type="info">
        <strong>Do not push the range past what works.</strong> The game only accepts a pickup from
        so far away. If <strong>Failed from</strong> keeps showing a value well below your slider,
        drag the slider down to it. A single failure is not proof, though: another player&rsquo;s
        loot or a full bag also leaves items on the ground. The measurements start again on every
        map change, whenever you switch Auto Loot on, and when a full bag gets space again.
      </Callout>

      <p className="text-sm text-gray-400 mb-4">
        Under <strong className="text-white">ADVANCED</strong> at the bottom of the card:
      </p>
      <DocTable
        headers={['Control', 'What it does']}
        rows={[
          [<strong className="text-white">Show radius on the ground</strong>, 'Draws your pickup range as a blue circle around your character, so you can see how far it reaches. Only drawn while Auto Loot is on. Default on.'],
          [<strong className="text-white">Items per burst</strong>, 'How many pickup requests go out at once, nearest items first (1–8). Default 3.'],
          [<strong className="text-white">Delay between bursts</strong>, 'The pause before the next burst (50–2000 ms). Default 150 ms.'],
        ]}
      />
      <p className="text-sm text-gray-400 mb-4">
        The defaults are fine for almost everyone. If items are left behind on a busy map, raise{' '}
        <strong className="text-white">Items per burst</strong> first.
      </p>

      <DocHeading>What to Pick Up</DocHeading>
      <p className="text-sm text-gray-400 mb-4">
        The <strong className="text-white">Categories</strong> dropdown lists every category, with an{' '}
        <strong className="text-white">All</strong> row on top. Its box shows what is ticked: the names
        if they fit, otherwise &ldquo;N selected&rdquo;, or <strong className="text-white">All</strong>{' '}
        / <strong className="text-white">None</strong>. For some categories, ticking them adds their
        own dropdown underneath, named after the category &mdash; so the rarity picker is only there
        when gear is actually being picked up.
      </p>
      <DocTable
        headers={['Category', 'Its own dropdown']}
        rows={[
          ['Gear, unidentified', 'Rarity: Common, Uncommon, Magic, Rare, Epic, Unique, Set, Mythic'],
          ['Gear, identified', 'Rarity, same list'],
          ['Gems', 'Grade: a Flawed, b Splintered, c Simple … i Royal, then Trapezoid, Imperial and Square grades (j–s)'],
          ['Runes', 'Grade: 0 Cracked, 1 Simple, 2 Solid, 3 Refined, 4 Flawless'],
          ['Jewels', '—'],
          ['Essences', 'Grade: Common, Uncommon, Magic, Rare, Epic'],
          ['Costumes', '—'],
          ['Materials', '—'],
          ['Pets', '—'],
          ['Mounts', '—'],
          ['Gold and keys', '—'],
          ['Quest and travel', '—'],
          ['Everything else', 'Anything that fits none of the above'],
        ]}
      />
      <Callout type="warning">
        <strong>A dropdown reading None picks up nothing.</strong> If <strong>Runes</strong> is ticked
        but the <strong>Runes</strong> dropdown under it says <strong>None</strong>, runes stay on
        the ground. After your first setup, open each dropdown that appeared and check its ticks
        &mdash; on a fresh setup <strong>Runes</strong> can start at <strong>None</strong>, and{' '}
        <strong>Gems</strong> with only the Trapezoid, Imperial and Square grades ticked.
      </Callout>
      <Callout type="info">
        <strong>When in doubt, it picks up.</strong> If the bot cannot read an item&rsquo;s rarity or
        grade, Auto Loot takes it anyway: a missed drop is lost, an extra item only costs a slot.
        Selling works the other way round and keeps anything it is unsure about.
      </Callout>
      <Callout type="info">
        <strong>Rarity names changed.</strong> Older versions used names like Improved, Extraordinary
        and Legendary. The list now uses the game&rsquo;s own names. Your ticks were kept &mdash; only
        the labels changed &mdash; so check the list once if you set it up a while ago.
      </Callout>

      <DocHeading>Single Items: the Item List</DocHeading>
      <p className="text-sm text-gray-400 mb-4">
        Categories are broad. To pick up (or ignore) one particular item, click{' '}
        <strong className="text-white">EDIT ITEM LIST</strong> at the bottom of the card. It opens{' '}
        <strong className="text-white">FARM &rsaquo; Item lists</strong> on the pickup side, where you
        mark single items:
      </p>
      <DocTable
        headers={['Rule', 'What happens on the ground']}
        rows={[
          [<strong className="text-white">TAKE</strong>, 'Always picked up — even if its category is unticked or its rarity/grade is switched off.'],
          [<strong className="text-white">SKIP</strong>, 'Never picked up, whatever the categories say.'],
          ['Neither', 'The categories above decide.'],
        ]}
      />
      <p className="text-sm text-gray-400 mb-4">
        <strong className="text-white">Item rules</strong> under the button shows how many pickup
        rules you have. See{' '}
        <button onClick={() => navigate('item-lists')} className="text-brand-300 hover:text-brand-200 underline underline-offset-2">Item Lists</button>{' '}
        for how to find items and set rules.
      </p>

      <Callout type="info">
        <strong>Selling uses the same categories, with separate settings</strong>: what you pick up
        and what you sell are different decisions. See{' '}
        <button onClick={() => navigate('auto-sell')} className="text-brand-300 hover:text-brand-200 underline underline-offset-2">Auto Sell</button>.
      </Callout>

      <DocHeading>When the Bag Is Full</DocHeading>
      <p className="text-sm text-gray-400 mb-4">
        A full bag does not stop everything. Only <strong className="text-white">gear</strong> is held
        back, because every piece needs its own slot &mdash; those show up under{' '}
        <strong className="text-white">Needs a free slot</strong>. Everything else &mdash; gold, keys,
        gems, materials and items that stack &mdash; is still attempted. A notification warns you:
        &ldquo;Bag is full - only equipment is skipped. Everything else is still attempted.&rdquo;
        As soon as a slot frees up, everything that failed while the bag was full is tried again
        straight away.
      </p>
      <p className="text-sm text-gray-400 mb-4">
        Auto Loot does not empty the bag itself. These features do, and both keep working during an
        Autopilot run:
      </p>
      <DocTable
        headers={['Feature', 'What it does']}
        rows={[
          [<button onClick={() => navigate('auto-sell')} className="text-brand-300 hover:text-brand-200 underline underline-offset-2">Auto Sell</button>, 'Sells the items you choose — no merchant visit or open shop needed.'],
          [<button onClick={() => navigate('auto-melt')} className="text-brand-300 hover:text-brand-200 underline underline-offset-2">Melt</button>, 'Melts runes, gear and gems right in the bag — no merchant needed.'],
        ]}
      />

      <DocHeading>Why Doesn&rsquo;t It Pick That Up?</DocHeading>
      <DocTable
        headers={['What you see', 'Likely reason']}
        rows={[
          ['The item is in range, but Matching filter does not count it', 'Its category is unticked, its rarity/grade dropdown excludes it, or the item list has it on SKIP.'],
          ['On cooldown stays above zero', 'The item stays on the ground after several tries — usually another player’s loot. After three tries it waits about 20 seconds, then tries again.'],
          ['Items just outside the circle are left behind', 'Auto Loot never walks to items. Move closer, or raise Pick up within (up to what Confirmed up to shows).'],
          ['Only gear is left on the ground', 'The bag is full — check Bag free and Needs a free slot.'],
          ['Settings look different after loading a profile', 'Farm profiles store everything on this tab except the on/off switch and the toggle key.'],
        ]}
      />
      <Callout type="info">
        All settings on this tab (except the on/off switch and the toggle key), together with your
        item list, can be saved and swapped as a{' '}
        <button onClick={() => navigate('farm-profiles')} className="text-brand-300 hover:text-brand-200 underline underline-offset-2">Farm Profile</button>{' '}
        &mdash; for example one for bloodshed runs and one for boss rooms.
      </Callout>
    </div>
  );
}

export default PageAutoLoot;
